import { CONFIG_HEADER } from '@vuejs/helpers/_constants'

import type {
	Agency,
	AgencyInformation,
	CurrentUser,
} from '@/javascript/interfaces'

import type { ApiClient, ResponseData } from '../api'
import {
	apiRouteBuilder,
	postMemberTags,
	postResendWelcomeMessage,
	postSendPasswordDetails,
	putGroupAssignment,
} from '../apiRoutes'
import { getDeactivatedMemberList } from '../apiRoutes'
import type { ComplianceType } from '../courses/interfaces'
import type { Member, MemberDetails } from '../members/interfaces'
import type { Group } from '../team/interfaces'
import type { Note } from './interfaces'

export async function getAgencyInsights(api: ApiClient, agencyId: number) {
	const { data } = await api.get<ApiAgencyInsightsResponse>(`/api/agencies/${agencyId}/insights`)

	return data
}

export async function getDeactivatedMembers(api: ApiClient, agencyId: number) {
	const { data } = await api.get<ApiGetDeactivatedResponse>(getDeactivatedMemberList, { id: agencyId })

	return data
}

export async function getMemberDetails(api: ApiClient, route: string) {
	const { data } = await api.get<MemberDetails>(route)

	return data
}

export async function postChangePassword(
	api: ApiClient,
	route: string,
	payload: ApiPostChangePasswordPayload
) {
	const response: ResponseData = await api.post(route, { caregiver_id: payload.memberId, password: payload.password }, CONFIG_HEADER)

	return response
}

export async function postMemberAction(
	api: ApiClient,
	route: string,
	payload: ApiPostActionPayload
) {
	const response: ResponseData = await api.post(route, { caregiver_ids: payload.memberIds }, CONFIG_HEADER)

	return response
}

export async function postNotes(api: ApiClient, route: string, { memberId, notes }: ApiPostNotesOptions) {
	await api.post<void>(
		route,
		{
			caregiver_id: memberId,
			notes,
		}
	)
}

export async function postResendMemberWelcomeMessage(api: ApiClient, payload: ApiPostProfilePayload) {
	await api.post(postResendWelcomeMessage, { caregiver_id: payload.memberId })
}

export async function postSendPasswordResetDetails(api: ApiClient, payload: ApiPostProfilePayload) {
	await api.post(postSendPasswordDetails, { caregiver_id: payload.memberId })
}

export async function postSendTextMessage(
	api: ApiClient,
	route: string,
	payload: ApiPostSendTextMessagePayload
): Promise<ApiPostSendTextMessageResponse> {
	const response = await api.post<ApiPostSendTextMessageResponse>(route, {
		add_all_caregivers: payload.addAllMembers,
		caregiver_ids: payload.memberIds,
		groupId: payload.groupId,
		message: payload.message,
	})

	return response.data
}

export async function postTag(api: ApiClient, tagName: string) {
	await api.post<void, ApiPostTagPayload>(postMemberTags, { tag_name: tagName })
}

export async function postTransferMember(
	api: ApiClient,
	route: string,
	payload: ApiPostTransferPayload
) {
	const response: ResponseData = await api.post(route, {
		caregiver_id: payload.memberId,
		new_agency_id: payload.newAgencyId,
	}, CONFIG_HEADER)

	return response
}

export async function putMoveToGroup(
	api: ApiClient,
	payload: ApiPutMoveToGroupPayload
) {
	const route = apiRouteBuilder(putGroupAssignment, { agencyId: payload.agencyId })

	await api.put(route, {
		agency_id: payload.agencyId,
		caregiver_group_id: payload.groupId,
		caregiver_ids: payload.memberIds,
	})
}

export interface ApiAgencyInsightsResponse {
	agency_created_at: string
	agency: Agency
	agencyId: number
	agencyInformation: AgencyInformation
	agencyName: string
	annual_due_date_soon_in_days: number
	can_modify_compliance_curriculum: boolean
	compliance_types: {
		annual: ComplianceType
		initial: ComplianceType
		none: ComplianceType
	}
	courseStatus: {
		complete: string
		incomplete: string
	},
	current_user: CurrentUser,
	flash_messages: string[]
	groups: Group[]
	is_advance_reporting: boolean
	is_advanced_automation_enabled: boolean
	is_hire_date_the_base_date_for_compliance_annual_due_date: boolean
	is_specialized_certifications_feature_enabled: boolean
	last_printed: {
		completion_certificate: string
		compliance_report: string
		incomplete: string
	}
	lms_names: Record<string, string>
	manageGroups: boolean
	membersCount: {
		dueSoon: number
		overdue: number
		total: number
		unstarted: number
		untrained: number
	}
	members: Member[]
	paths: {
		add_courses: string
		available_courses: string
		available_specialized_certifications: string
		create_curriculum_track: string
		disable_course: string
		duplicate_curriculum_track: string
		group_assignment: string
		management_annual: string
		management_initial: string
		new_member: string
		offline_courses: string
		print_completion_certificate: string
		print_compliance_report: string
		update_course: string
		waive_courses: string
	}
	show_due_date: boolean
	show_initial_training_due_date: boolean
	twilio_success_code: string
	twilio_user_error_messages: string
	waive_types: {
		agency: ComplianceType
		careacademy: ComplianceType
		shared_completion: ComplianceType
	}
}

export interface ApiGetDeactivatedResponse {
	caregivers: Member[],
	softwareProviderName: string,
}

export interface ApiPostActionPayload {
	memberIds: number[]
}

export interface ApiPostChangePasswordPayload {
	memberId: string
	password: string
}

export interface ApiPostNotesOptions {
	memberId: number
	notes: Note[]
}

export interface ApiPostNotePayload extends ApiPostProfilePayload {
	notes: Note[]
}

export interface ApiPostProfilePayload {
	memberId: number
}

export interface ApiPostSendTextMessagePayload {
	addAllMembers?: boolean
	groupId?: number
	memberIds?: string
	message: string
}

export interface ApiPostSendTextMessageResponse {
	error: string | null,
	message: string | null,
	updated_caregivers: Record<number, number | string>[][]
}

export interface ApiPostTagPayload {
	tag_name: string
}

export interface ApiPostTransferPayload extends ApiPostProfilePayload {
	newAgencyId: number | undefined
}

export interface ApiPutMoveToGroupPayload {
	agencyId: number
	groupId: number | null
	memberIds: number[]
}
